import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { useLocation } from 'react-router'
import {
  Box,
  Text,
  IconErrorOutline,
  IconInfoInverted,
  TextLinkButton
} from '@machobear/design-system'
import { useHistory } from 'react-router-dom'
import { fontWeights, colors } from '@/styles'
import Skeleton from '@/components/skeleton'
import config from '@/config'

const { APP_PATH } = config

const TextWrapper = styled.div`
  width: 100%;
  margin: ${props => (props.margin ? props.margin : '0 0 8px 0')};
  padding: 8px 10px;
  background: ${colors.grey96};
  border-width: 1px 0px;
  border-style: solid;
  border-color: ${colors.grey88};
`

const LabelText = styled.span`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 20px;
  color: ${colors.grey16};
  margin: 0;
  ${props =>
    props.isLocked &&
    `
    filter: blur(10px);
    color: ${colors.black};
    background-color: ${colors.grey96};
    -webkit-touch-callout: none;
    -webkit-user-select: none; 
    -khtml-user-select: none; 
    -moz-user-select: none; 
    -ms-user-select: none; 
    user-select: none; 
    cursor: not-allowed;
  `}
`

const WarningText = styled(LabelText)`
  color: ${colors.red40};
`

const Empty = ({ application, data, isLocked, isLoading, standalone }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const documentsUrl = `${location?.pathname?.substring(
    0,
    location?.pathname?.lastIndexOf('/')
  )}/documents`
  const chatUrl = `/chat/${application?.timeline_room}`
  const handleDocumentsClick = () => window.open(`${APP_PATH}${documentsUrl}`, '_blank')
  const handleChatClick = () => window.open(`${APP_PATH}${chatUrl}`, '_blank')
  const handleResubmitClick = () => history.push(`/trust-score/credit-check`)
  const isNoCreditHistory =
    application?.trust_score?.status === 'no_credit_history' ||
    application?.status === 'no_credit_history' ||
    data?.status === 'no_credit_history'
  if (isLoading) return <Skeleton width="100%" height={40} />
  return isNoCreditHistory ? (
    <>
      <TextWrapper margin={'20px 0 20px 0'} isLocked={isLocked}>
        <LabelText>
          <Trans i18nKey={'l.application.credit_summary.no_credit_history.label2'} t={t}>
            This applicant has <WarningText>no credit history available</WarningText> on Equifax®.
          </Trans>
        </LabelText>
      </TextWrapper>
      {!standalone && (
        <Box
          background="infoLight"
          borderRadius="large"
          padding="small"
          display="flex"
          gap="xxsmall"
        >
          <IconInfoInverted />
          <Text tone="info" size="small" baseline={false}>
            <Trans i18nKey={'l.application.credit_summary.no_credit_history.view_docs2'} t={t}>
              You can still view financial documents that have been verified by the liv.rent team.{' '}
              <TextLinkButton onClick={handleDocumentsClick}>View documents</TextLinkButton>
            </Trans>
          </Text>
        </Box>
      )}
    </>
  ) : (
    <>
      <Box
        background="criticalLight"
        borderRadius="large"
        padding="small"
        display="flex"
        gap="xxsmall"
      >
        <IconErrorOutline />
        <Text tone="critical" size="small" baseline={false}>
          {standalone ? (
            <Trans i18nKey={'l.standalone.credit_summary.not_enough_info.label2'} t={t}>
              We were unable to retrieve the tenant’s information from Equifax®. Please try again
              and resubmit their credit check details.{' '}
              <TextLinkButton onClick={handleResubmitClick}>Resubmit Trust Score</TextLinkButton>
            </Trans>
          ) : (
            <Trans i18nKey={'l.application.credit_summary.not_enough_info.label2'} t={t}>
              We were unable to retrieve the applicant’s information from Equifax®. Here’s what you
              can do:
              <br />
              1. Ask the applicant to resubmit their credit check details.{' '}
              <TextLinkButton onClick={handleChatClick}>Chat now</TextLinkButton> <br />
              2. Review financial documents verified by the liv.rent team.{' '}
              <TextLinkButton onClick={handleDocumentsClick}>View documents</TextLinkButton>
            </Trans>
          )}
        </Text>
      </Box>
    </>
  )
}

export default Empty
